<template>
  <div class="ar-page">
    <!-- Encabezado -->
    <div class="ar-header">
      <div class="ar-header__info">
        <h2 class="ar-header__title">Solicitudes de acceso</h2>
        <p class="ar-header__sub">
          Usuarios que pidieron acceso sin código de invitación
        </p>
      </div>
      <div class="ar-header__actions">
        <span v-if="pendingCount" class="ar-pending-pill">
          <i class="fas fa-clock me-1"></i>{{ pendingCount }} pendiente{{ pendingCount !== 1 ? 's' : '' }}
        </span>
        <button class="ar-icon-btn" title="Actualizar" @click="load" :disabled="loading">
          <i class="fas fa-sync-alt" :class="{ 'fa-spin': loading }"></i>
        </button>
      </div>
    </div>

    <!-- Búsqueda + filtros -->
    <div class="ar-toolbar">
      <div class="ar-search">
        <i class="fas fa-search ar-search__icon"></i>
        <input
          v-model="search"
          type="text"
          class="ar-search__input"
          placeholder="Buscar por nombre, email o negocio…"
        />
        <button v-if="search" type="button" class="ar-search__clear" title="Limpiar" @click="search = ''">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <div class="ar-chips">
        <button
          v-for="chip in quickFilters"
          :key="chip.key"
          type="button"
          class="ar-chip"
          :class="{ 'ar-chip--active': activeFilter === chip.key }"
          @click="activeFilter = chip.key"
        >
          <i :class="chip.icon" class="me-1"></i>{{ chip.label }}
          <span v-if="chip.key !== 'all'" class="ar-chip__count">{{ countByStatus(chip.key) }}</span>
        </button>
      </div>
    </div>

    <!-- Cargando -->
    <div v-if="loading" class="ar-grid">
      <div v-for="n in 6" :key="n" class="ar-card ar-card--skeleton">
        <div class="ar-skeleton ar-skeleton--avatar"></div>
        <div class="ar-skeleton ar-skeleton--line w-60"></div>
        <div class="ar-skeleton ar-skeleton--line w-40"></div>
        <div class="ar-skeleton ar-skeleton--line w-80"></div>
      </div>
    </div>

    <!-- Error -->
    <div v-else-if="error" class="ar-state ar-state--error">
      <i class="fas fa-exclamation-circle fa-2x mb-3"></i>
      <p>{{ error }}</p>
      <button class="btn btn-outline-light btn-sm mt-2" @click="load">
        <i class="fas fa-redo me-1"></i>Reintentar
      </button>
    </div>

    <!-- Vacío -->
    <div v-else-if="!displayedRequests.length" class="ar-state">
      <div class="ar-state__icon"><i class="fas fa-inbox"></i></div>
      <h3>{{ emptyTitle }}</h3>
      <p>{{ emptyText }}</p>
    </div>

    <!-- Grilla de tarjetas -->
    <div v-else class="ar-grid">
      <article
        v-for="req in displayedRequests"
        :key="req.id"
        class="ar-card"
        :class="{ 'ar-card--muted': req.status !== 'pending' }"
        @click="openReview(req)"
      >
        <header class="ar-card__head">
          <div class="ar-card__identity">
            <span class="ar-avatar">{{ initials(req) }}</span>
            <div class="ar-card__names">
              <span class="ar-card__name">{{ req.first_name }} {{ req.last_name }}</span>
              <span class="ar-card__email">{{ req.email }}</span>
            </div>
          </div>
          <span class="ar-status" :class="statusClass(req.status)">
            <i :class="statusIcon(req.status)" class="me-1"></i>{{ req.status_display }}
          </span>
        </header>

        <div class="ar-card__tags">
          <span class="ar-role-tag" :style="{ background: roleColor(req), color: '#111' }">
            {{ req.role_label }}
          </span>
          <span class="ar-card__date"><i class="far fa-calendar-alt me-1"></i>{{ formatDate(req.created_at) }}</span>
        </div>

        <ul class="ar-card__meta">
          <li v-if="req.phone_number"><i class="fas fa-phone me-2"></i>{{ req.phone_number }}</li>
          <li v-if="req.instagram"><i class="fab fa-instagram me-2"></i>{{ req.instagram }}</li>
          <li v-if="req.business_name"><i class="fas fa-store me-2"></i>{{ req.business_name }}</li>
        </ul>

        <p v-if="req.extra_info" class="ar-card__note">{{ truncate(req.extra_info, 110) }}</p>

        <footer class="ar-card__footer" @click.stop>
          <template v-if="req.status === 'pending'">
            <button type="button" class="ar-btn ar-btn--primary" @click="openReview(req)">
              <i class="fas fa-clipboard-check me-1"></i>Revisar
            </button>
          </template>
          <template v-else-if="req.approved_user">
            <router-link :to="`/panel/usuarios/${req.approved_user}`" class="ar-btn ar-btn--ghost">
              <i class="fas fa-user me-1"></i>Ver usuario
            </router-link>
            <button type="button" class="ar-btn ar-btn--ghost" @click="openReview(req)">
              <i class="fas fa-eye me-1"></i>Detalle
            </button>
          </template>
          <template v-else>
            <span class="ar-card__rejected">
              <i class="fas fa-times-circle me-1"></i>{{ truncate(req.rejection_reason || 'Rechazada', 80) }}
            </span>
            <button type="button" class="ar-btn ar-btn--ghost" @click="openReview(req)">
              <i class="fas fa-eye me-1"></i>Detalle
            </button>
          </template>
        </footer>
      </article>
    </div>

    <!-- Modal de revisión -->
    <div v-if="reviewTarget" class="panel-modal-overlay" @mousedown.self="closeReview">
      <div class="panel-modal panel-modal-lg ar-review">
        <div class="ar-review__head">
          <span class="ar-avatar ar-avatar--lg">{{ initials(reviewTarget) }}</span>
          <div class="ar-review__title">
            <h3>{{ reviewTarget.first_name }} {{ reviewTarget.last_name }}</h3>
            <span class="ar-review__email">{{ reviewTarget.email }}</span>
          </div>
          <span class="ar-status" :class="statusClass(reviewTarget.status)">
            <i :class="statusIcon(reviewTarget.status)" class="me-1"></i>{{ reviewTarget.status_display }}
          </span>
        </div>

        <!-- Info -->
        <div class="ar-info">
          <div class="ar-info__item">
            <span class="ar-info__label">Perfil solicitado</span>
            <span class="ar-role-tag" :style="{ background: roleColor(reviewTarget), color: '#111' }">
              {{ reviewTarget.role_label }}
            </span>
          </div>
          <div class="ar-info__item">
            <span class="ar-info__label">Teléfono</span>
            <span class="ar-info__value">{{ reviewTarget.phone_number || '—' }}</span>
          </div>
          <div class="ar-info__item">
            <span class="ar-info__label">DNI</span>
            <span class="ar-info__value">{{ reviewTarget.dni || '—' }}</span>
          </div>
          <div class="ar-info__item">
            <span class="ar-info__label">Instagram</span>
            <span class="ar-info__value">{{ reviewTarget.instagram || '—' }}</span>
          </div>
          <div class="ar-info__item ar-info__item--full">
            <span class="ar-info__label">Negocio</span>
            <span class="ar-info__value">{{ reviewTarget.business_name || '—' }}</span>
          </div>
          <div class="ar-info__item ar-info__item--full">
            <span class="ar-info__label">Solicitada el</span>
            <span class="ar-info__value">{{ formatDate(reviewTarget.created_at) }}</span>
          </div>
          <div v-if="reviewTarget.extra_info" class="ar-info__item ar-info__item--full">
            <span class="ar-info__label">Comentarios</span>
            <span class="ar-info__value ar-info__value--note">{{ reviewTarget.extra_info }}</span>
          </div>
          <div v-if="reviewTarget.status === 'rejected' && reviewTarget.rejection_reason" class="ar-info__item ar-info__item--full">
            <span class="ar-info__label">Motivo del rechazo</span>
            <span class="ar-info__value ar-info__value--danger">{{ reviewTarget.rejection_reason }}</span>
          </div>
        </div>

        <!-- Acciones pendientes -->
        <template v-if="reviewTarget.status === 'pending'">
          <div class="ar-review__actions">
            <label class="ar-review__role-label" for="ar-role-select">Rol a asignar</label>
            <select id="ar-role-select" v-model="approveRole" class="form-select">
              <option v-for="r in roleOptions" :key="r.slug" :value="r.slug">{{ r.label }}</option>
            </select>
          </div>

          <!-- Rechazo inline -->
          <transition name="ar-fade">
            <div v-if="showReject" class="ar-review__reject">
              <label class="ar-review__role-label" for="ar-reject-reason">Motivo del rechazo (se envía por email)</label>
              <textarea
                id="ar-reject-reason"
                v-model="rejectReason"
                class="form-control"
                rows="3"
                placeholder="Ej: no cumple con los requisitos de venta mayorista…"
              ></textarea>
            </div>
          </transition>

          <div class="panel-modal-actions">
            <button type="button" class="btn btn-outline-danger" @click="toggleReject">
              <i class="fas fa-times me-1"></i>{{ showReject ? 'Cancelar rechazo' : 'Rechazar' }}
            </button>
            <button
              v-if="showReject"
              type="button"
              class="btn btn-danger"
              :disabled="rejecting || !rejectReason.trim()"
              @click="confirmReject"
            >
              <span v-if="rejecting"><i class="fas fa-spinner fa-spin me-1"></i>Rechazando…</span>
              <span v-else>Confirmar rechazo</span>
            </button>
            <button
              v-else
              type="button"
              class="btn btn-success"
              :disabled="approving"
              @click="confirmApprove"
            >
              <span v-if="approving"><i class="fas fa-spinner fa-spin me-1"></i>Aprobando…</span>
              <span v-else><i class="fas fa-check me-1"></i>Aprobar y crear cuenta</span>
            </button>
          </div>
        </template>

        <!-- Acciones para procesadas -->
        <div v-else class="panel-modal-actions">
          <button type="button" class="btn btn-secondary" @click="closeReview">Cerrar</button>
          <router-link
            v-if="reviewTarget.approved_user"
            :to="`/panel/usuarios/${reviewTarget.approved_user}`"
            class="btn btn-primary"
          >
            <i class="fas fa-user me-1"></i>Ver usuario
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useToast } from 'vue-toastification'
import { getAccessRequests, approveAccessRequest, rejectAccessRequest } from '@/services/accessRequests'
import { getCustomerRoles } from '@/services/invitations'

const toast = useToast()

const requests = ref([])
const loading = ref(true)
const error = ref(null)
const search = ref('')
const activeFilter = ref('pending')

const roleOptions = ref([])

const reviewTarget = ref(null)
const approveRole = ref('')
const showReject = ref(false)
const rejectReason = ref('')
const approving = ref(false)
const rejecting = ref(false)

const quickFilters = [
  { key: 'pending', label: 'Pendientes', icon: 'fas fa-clock' },
  { key: 'approved', label: 'Aprobadas', icon: 'fas fa-check' },
  { key: 'rejected', label: 'Rechazadas', icon: 'fas fa-times' },
  { key: 'all', label: 'Todas', icon: 'fas fa-list' },
]

const countByStatus = (status) => requests.value.filter((r) => r.status === status).length

const pendingCount = computed(() => countByStatus('pending'))

const displayedRequests = computed(() => {
  const q = search.value.trim().toLowerCase()
  return requests.value.filter((req) => {
    if (activeFilter.value !== 'all' && req.status !== activeFilter.value) return false
    if (!q) return true
    const haystack = [req.first_name, req.last_name, req.email, req.business_name, req.phone_number]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()
    return haystack.includes(q)
  })
})

const emptyTitle = computed(() => {
  if (search.value.trim()) return 'Sin resultados'
  if (activeFilter.value === 'approved') return 'Todavía no aprobaste solicitudes'
  if (activeFilter.value === 'rejected') return 'No hay solicitudes rechazadas'
  return 'No hay solicitudes pendientes'
})

const emptyText = computed(() => {
  if (search.value.trim()) return 'Probá con otra búsqueda.'
  if (activeFilter.value !== 'pending') return 'Cambiá el filtro para ver otras solicitudes.'
  return 'Cuando un usuario pida acceso sin código, va a aparecer acá para que lo revises.'
})

const initials = (req) => {
  const a = (req.first_name || '').trim().charAt(0)
  const b = (req.last_name || '').trim().charAt(0)
  return `${a}${b}`.toUpperCase() || '?'
}

const roleColor = (req) => req.role_color || '#ffec00'

const statusClass = (status) => {
  if (status === 'approved') return 'ar-status--ok'
  if (status === 'rejected') return 'ar-status--err'
  return 'ar-status--warn'
}

const statusIcon = (status) => {
  if (status === 'approved') return 'fas fa-check-circle'
  if (status === 'rejected') return 'fas fa-times-circle'
  return 'fas fa-clock'
}

const formatDate = (iso) => {
  if (!iso) return '—'
  const d = new Date(iso)
  return d.toLocaleDateString('es-AR', { day: '2-digit', month: 'short', year: 'numeric' })
}

const truncate = (text, max) => (text && text.length > max ? `${text.slice(0, max).trim()}…` : text)

const load = async () => {
  loading.value = true
  error.value = null
  try {
    const data = await getAccessRequests({ page_size: 200 })
    requests.value = data.results || data || []
  } catch (e) {
    error.value = e?.response?.data?.detail || 'No se pudieron cargar las solicitudes.'
  } finally {
    loading.value = false
  }
}

const loadRoles = async () => {
  try {
    const data = await getCustomerRoles({ active_only: 'true' })
    roleOptions.value = Array.isArray(data) ? data : (data.results || [])
  } catch {
    roleOptions.value = []
  }
}

const openReview = (req) => {
  reviewTarget.value = req
  approveRole.value = req.role_slug || req.requested_role || ''
  showReject.value = false
  rejectReason.value = ''
  if (!roleOptions.value.length) loadRoles()
}

const closeReview = () => {
  reviewTarget.value = null
  approveRole.value = ''
  showReject.value = false
  rejectReason.value = ''
}

const toggleReject = () => {
  showReject.value = !showReject.value
  if (!showReject.value) rejectReason.value = ''
}

const confirmApprove = async () => {
  if (!reviewTarget.value) return
  approving.value = true
  try {
    await approveAccessRequest(reviewTarget.value.id, { role: approveRole.value })
    toast.success('Solicitud aprobada. Se creó la cuenta y se envió el email de acceso.')
    closeReview()
    await load()
  } catch (e) {
    toast.error(e?.response?.data?.detail || 'No se pudo aprobar la solicitud.')
  } finally {
    approving.value = false
  }
}

const confirmReject = async () => {
  if (!reviewTarget.value || !rejectReason.value.trim()) return
  rejecting.value = true
  try {
    await rejectAccessRequest(reviewTarget.value.id, { reason: rejectReason.value.trim() })
    toast.success('Solicitud rechazada. Se envió el email al solicitante.')
    closeReview()
    await load()
  } catch (e) {
    toast.error(e?.response?.data?.detail || 'No se pudo rechazar la solicitud.')
  } finally {
    rejecting.value = false
  }
}

onMounted(() => {
  load()
  loadRoles()
})
</script>

<style scoped>
.ar-page {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

/* Encabezado */
.ar-header {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}

.ar-header__title {
  margin: 0 0 0.15rem;
  font-size: 1.35rem;
  font-weight: 700;
  color: var(--white-color);
}

.ar-header__sub {
  margin: 0;
  font-size: 0.85rem;
  color: var(--panel-text-muted, rgba(255, 255, 255, 0.55));
}

.ar-header__actions {
  display: flex;
  align-items: center;
  gap: 0.6rem;
}

.ar-pending-pill {
  display: inline-flex;
  align-items: center;
  padding: 0.3rem 0.7rem;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 600;
  background: rgba(245, 158, 11, 0.14);
  color: #f59e0b;
  border: 1px solid rgba(245, 158, 11, 0.28);
}

.ar-icon-btn {
  width: 38px;
  height: 38px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  border: 1px solid var(--panel-border);
  background: rgba(0, 0, 0, 0.2);
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;
  transition: color 0.15s, border-color 0.15s;
}

.ar-icon-btn:hover {
  color: var(--white-color);
  border-color: rgba(255, 255, 255, 0.25);
}

/* Toolbar: búsqueda + chips */
.ar-toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 0.85rem;
  align-items: center;
}

.ar-search {
  position: relative;
  flex: 1;
  min-width: 220px;
}

.ar-search__icon {
  position: absolute;
  left: 0.85rem;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(255, 255, 255, 0.4);
  font-size: 0.85rem;
}

.ar-search__input {
  width: 100%;
  padding: 0.6rem 2.3rem 0.6rem 2.4rem;
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid var(--panel-border);
  border-radius: 12px;
  color: var(--white-color);
  font-size: 0.9rem;
}

.ar-search__input:focus {
  outline: none;
  background: rgba(0, 0, 0, 0.28);
  border-color: var(--primary-color);
  box-shadow: 0 0 0 2px rgba(255, 236, 0, 0.12);
}

.ar-search__clear {
  position: absolute;
  right: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.45);
  cursor: pointer;
  padding: 0.25rem 0.4rem;
  border-radius: 6px;
}

.ar-search__clear:hover {
  color: var(--white-color);
}

.ar-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}

.ar-chip {
  display: inline-flex;
  align-items: center;
  border: 1px solid var(--panel-border);
  background: rgba(0, 0, 0, 0.15);
  color: rgba(255, 255, 255, 0.65);
  border-radius: 999px;
  padding: 0.4rem 0.85rem;
  font-size: 0.82rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s, color 0.15s, border-color 0.15s;
}

.ar-chip:hover {
  color: var(--white-color);
  border-color: rgba(255, 255, 255, 0.2);
}

.ar-chip--active {
  background: var(--primary-color);
  border-color: var(--primary-color);
  color: var(--black-color);
}

.ar-chip__count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  margin-left: 0.4rem;
  padding: 0 0.3rem;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 700;
  background: rgba(0, 0, 0, 0.18);
}

/* Grilla */
.ar-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.9rem;
}

@media (min-width: 768px) {
  .ar-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (min-width: 1200px) {
  .ar-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

/* Tarjeta */
.ar-card {
  background: var(--panel-surface);
  border: 1px solid var(--panel-border);
  border-radius: 0;
  padding: 1.1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  cursor: pointer;
  transition: transform 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;
}

.ar-card:hover {
  transform: translateY(-2px);
  border-color: rgba(255, 236, 0, 0.35);
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.35);
}

.ar-card--muted {
  opacity: 0.82;
}

.ar-card__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.6rem;
}

.ar-card__identity {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  min-width: 0;
}

.ar-card__names {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.ar-card__name {
  font-weight: 600;
  color: var(--white-color);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ar-card__email {
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.55);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ar-avatar {
  flex-shrink: 0;
  width: 42px;
  height: 42px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--primary-color, #ffec00);
  color: #111;
  font-weight: 700;
  font-size: 0.95rem;
}

.ar-avatar--lg {
  width: 52px;
  height: 52px;
  font-size: 1.15rem;
}

/* Estado */
.ar-status {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  font-size: 0.72rem;
  font-weight: 600;
  border-radius: 999px;
  padding: 0.25rem 0.6rem;
  white-space: nowrap;
}

.ar-status--warn {
  background: rgba(245, 158, 11, 0.14);
  color: #f59e0b;
}

.ar-status--ok {
  background: rgba(16, 185, 129, 0.14);
  color: #10b981;
}

.ar-status--err {
  background: rgba(239, 68, 68, 0.14);
  color: #f87171;
}

/* Tags + fecha */
.ar-card__tags {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.ar-role-tag {
  display: inline-block;
  padding: 0.22rem 0.6rem;
  border-radius: 4px;
  font-size: 0.72rem;
  font-weight: 700;
}

.ar-card__date {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.45);
}

/* Meta */
.ar-card__meta {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  font-size: 0.82rem;
  color: rgba(255, 255, 255, 0.75);
}

.ar-card__meta li {
  display: flex;
  align-items: center;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.ar-card__meta li i {
  color: rgba(255, 255, 255, 0.4);
  width: 1.1rem;
  text-align: center;
  flex-shrink: 0;
}

.ar-card__note {
  margin: 0;
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.6);
  font-style: italic;
  line-height: 1.4;
}

.ar-card__rejected {
  font-size: 0.78rem;
  color: #f87171;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

/* Footer de tarjeta */
.ar-card__footer {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: auto;
  padding-top: 0.75rem;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}

.ar-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.45rem 0.85rem;
  border-radius: 8px;
  border: 1px solid transparent;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, color 0.15s;
  text-decoration: none;
}

.ar-btn--primary {
  background: var(--primary-color, #ffec00);
  color: #111;
  border-color: var(--primary-color, #ffec00);
}

.ar-btn--primary:hover {
  background: #ffd500;
  color: #111;
}

.ar-btn--ghost {
  background: transparent;
  color: rgba(255, 255, 255, 0.75);
  border-color: var(--panel-border);
}

.ar-btn--ghost:hover {
  color: var(--white-color);
  border-color: rgba(255, 255, 255, 0.3);
}

/* Estados / vacío / error */
.ar-state {
  padding: 3rem 1.5rem;
  text-align: center;
  border: 1px dashed rgba(255, 255, 255, 0.14);
  border-radius: 12px;
  color: rgba(255, 255, 255, 0.6);
}

.ar-state h3 {
  margin: 0 0 0.4rem;
  color: var(--white-color);
  font-size: 1.05rem;
}

.ar-state p {
  margin: 0;
  font-size: 0.88rem;
}

.ar-state__icon {
  width: 64px;
  height: 64px;
  margin: 0 auto 1rem;
  border-radius: 50%;
  background: rgba(255, 236, 0, 0.1);
  color: var(--primary-color, #ffec00);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
}

.ar-state--error {
  color: #f87171;
}

/* Skeleton (mismo patrón shimmer que el resto del panel) */
.ar-skeleton {
  border-radius: 4px;
  height: 12px;
  background: linear-gradient(90deg, var(--black-color-2) 25%, rgba(255, 255, 255, 0.06) 50%, var(--black-color-2) 75%);
  background-size: 200% 100%;
  animation: ar-shimmer 1.4s infinite;
}

.ar-skeleton--avatar {
  width: 42px;
  height: 42px;
  border-radius: 50%;
}

.ar-skeleton--line {
  width: 100%;
}

.ar-skeleton--line.w-60 { width: 60%; }
.ar-skeleton--line.w-40 { width: 40%; }
.ar-skeleton--line.w-80 { width: 80%; }

@keyframes ar-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* Modal de revisión */
.ar-review__head {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  margin-bottom: 1.25rem;
}

.ar-review__title {
  flex: 1;
  min-width: 0;
}

.ar-review__title h3 {
  margin: 0 0 0.1rem;
  font-size: 1.15rem;
  color: var(--white-color);
}

.ar-review__email {
  font-size: 0.82rem;
  color: rgba(255, 255, 255, 0.55);
}

.ar-info {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
  margin-bottom: 1.25rem;
}

.ar-info__item {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  padding: 0.7rem 0.85rem;
  background: rgba(0, 0, 0, 0.16);
  border: 1px solid var(--panel-border);
  border-radius: 8px;
  min-width: 0;
}

.ar-info__item--full {
  grid-column: 1 / -1;
}

.ar-info__label {
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(255, 255, 255, 0.45);
}

.ar-info__value {
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.9);
  word-break: break-word;
}

.ar-info__value--note {
  font-style: italic;
  color: rgba(255, 255, 255, 0.75);
  white-space: pre-wrap;
}

.ar-info__value--danger {
  color: #f87171;
}

.ar-review__actions {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  margin-bottom: 0.25rem;
}

.ar-review__role-label {
  font-size: 0.78rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.7);
}

.ar-review__reject {
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid var(--panel-border);
}

.ar-review .form-control,
.ar-review .form-select {
  background: rgba(0, 0, 0, 0.25);
  border-color: var(--panel-border);
  color: var(--white-color);
}

.ar-review .form-control:focus,
.ar-review .form-select:focus {
  background: rgba(0, 0, 0, 0.3);
  border-color: var(--primary-color);
  color: var(--white-color);
  box-shadow: 0 0 0 2px rgba(255, 236, 0, 0.12);
}

.ar-fade-enter-active,
.ar-fade-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}

.ar-fade-enter-from,
.ar-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
